import { PlusOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { Button, Divider, message } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../context/app/context";
import ShelfContext from "../../../context/wms/shelf/context";
import ShelfContextProvider from "../../../context/wms/shelf/provider";
import { useIsMount } from "../../../utils/hooks/is-mount";
import ShelfTable from "../../../components/wms/shelf/shelf-table";
import SearchFilterCard from "../../../components/general/filter-card";
import Can from "../../../utils/rbac/can";
import PageHeader from "../../../components/general/layout/page-header";
import RefreshBtn from "../../../components/general/table-components/actions/refresh-btn";
import { Permissions } from "../../../utils/Permissions/Permissions";
import ShelfFilter from "../../../components/wms/shelf/shelf-filter";
import styles from "./style.module.scss";
import { hasRequiredStaticFilters } from "../../../utils/wms/sort-filter-guard";

interface IProps {
  children?: React.ReactNode;
}

const ShelfComponent: React.FC<IProps> = (props) => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const navigate = useNavigate();

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortEnabled, setSortEnabled] = useState(false);

  const { actions, loading, query } = useContext(ShelfContext);

  useEffect(() => {
    actions.setSearch(search);
  }, [search]);

  const isMounted = useIsMount();
  useEffect(() => {
    !isMounted && actions.getData();
  }, [query, direction]);

  const hasSortingScope = hasRequiredStaticFilters(query, ["storageUnitId"]);

  useEffect(() => {
    if (sortEnabled && !hasSortingScope) {
      setSortEnabled(false);
    }
  }, [hasSortingScope, sortEnabled]);

  const handleSortToggle = () => {
    if (sortEnabled) {
      setSortEnabled(false);
      return;
    }

    if (!hasSortingScope) {
      message.warning(t("wms_sort_shelves_filter_required"));
      return;
    }

    setSortEnabled(true);
  };

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("shelves")}
        subTitle={t("shelves_management")}
        extra={[
          <RefreshBtn
            key={0}
            loading={loading.includes("list")}
            onClick={() => actions.getData()}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Can key={3} perform={Permissions.CAN_VIEW_ALL_WMS_SHELVES}>
            <Divider type="vertical" />
            <Button
              type={sortEnabled ? "primary" : "default"}
              onClick={handleSortToggle}
              icon={<SortAscendingOutlined />}
            >
              {sortEnabled ? t("disable_sorting") : t("enable_sorting")}
            </Button>
          </Can>,
          <Can key={2} perform={Permissions.CAN_CREATE_WMS_SHELF}>
            <Divider key={1} type="vertical" />
            <Button
              type="primary"
              onClick={() => {
                navigate(`/wms/shelves/create`);
              }}
              icon={<PlusOutlined />}
            >
              {t("add_shelf")}
            </Button>
          </Can>,
        ]}
      />
      <SearchFilterCard
        formId="shelf-filter"
        searchValue={search}
        setSearchValue={setSearch}
        onReset={() => {
          actions.setQuery({
            page: query?.page,
            perPage: query?.perPage,
          });
        }}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
      >
        <ShelfFilter />
      </SearchFilterCard>

      <ShelfTable sortEnabled={sortEnabled} />
    </div>
  );
};

const ShelfPage = () => {
  return (
    <ShelfContextProvider>
      <ShelfComponent />
    </ShelfContextProvider>
  );
};

export default ShelfPage;
